import MaterialIconButton, {IconButtonProps as MuiIconButtonProps, IconButtonTypeMap} from '@mui/material/IconButton';
import {styled} from '@mui/material/styles';
import React from 'react';
import {FuelSize, getFuelSize} from '../types/general';
import {excludeComponentProps} from '../utils/excludeComponentProps';

type DefaultIconButtonProps = MuiIconButtonProps<IconButtonTypeMap['defaultComponent'], any>;
type BaseIconButtonProps = Omit<DefaultIconButtonProps, 'color' | 'size' |'disableElevation' |'disableFocusRipple' |'centerRipple' |'disableRipple' |'disableTouchRipple' |'focusRipple' |'TouchRippleProps'>;

export interface IconButtonProps extends BaseIconButtonProps {
    /* Fuel specific sizes */
    size?: FuelSize;
    /* Adds outline to IconButton */
    outlined?: boolean;
}

const StyledButton = styled(MaterialIconButton, {
    ...excludeComponentProps('size', 'variant', 'outlined')
})<IconButtonProps>(({
    theme, size: currentSize, outlined
}) => {
    const size = getFuelSize(currentSize);

    return {
        transition: 'unset',
        borderRadius: `${theme.tokens.FuelBorderRadiusMd}`,
        border: '1px solid transparent',
        display: 'inline-flex',
        alignItems: 'center',

        ...size === 'small' && {
            height: '2rem',
            width: '2rem',
            padding: '0.1875rem'
        },

        ...size === 'medium' && {
            height: '2.25rem',
            width: '2.25rem',
            padding: '0.3125rem'
        },

        ...size === 'large' && {
            height: '2.5rem',
            width: '2.5rem',
            padding: '0.4375rem'
        },

        ...Boolean(outlined) === true && {
            boxShadow: `${theme.tokens.FuelShadowXs}, rgba(0, 0, 0, 0.05) 0px 1px 0px 0px`,
            border: `1px solid ${theme.tokens.FuelColorBorderDark}`
        },
        '&:hover': {
            color: `${theme.tokens.FuelColorTextPrimary}`
        },
        '&:active': {
            backgroundColor: `${theme.tokens.FuelColorBackgroundActionSecondaryPressed}`
        },
        '&:focus': {
            outline: 'none'
        },
        '&[disabled], &.Mui-disabled': {
            color: `${theme.tokens.FuelColorTextActionSecondary}`,
            backgroundColor: 'transparent',
            opacity: 0.5,
            cursor: 'not-allowed',
            boxShadow: 'none',
            ...Boolean(outlined) === true && {
                borderColor: `${theme.tokens.FuelColorBorderLight}`,
                boxShadow: 'none'
            }
        },
        '&.Mui-focusVisible': {
            boxShadow: `${theme.tokens.FuelShadowFocused}`
        }
    };
});

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
    (
        {
            children,
            size = 'medium',
            ...props
        },
        ref
    ) => {
        return (
            <StyledButton
                {...props}
                ref={ref}
                size={size}
            >
                {children}
            </StyledButton>
        );
    });

IconButton.displayName = 'IconButton';
